export type AppTab = {
	title: string;
	fullPath: string;
	pinned: boolean;
};

const sessionTabs = useLocalStorage<AppTab[]>("session-tabs", []);

/**
 * Composable function to manage the application tabs
 * Tabs are added by vue-router navigation guards
 */
export const useAppTabs = () => {
	const { push, currentRoute, back, previousUrl } = useRouter();

	const addTab = (tab: AppTab) => {
		tab.pinned = true; // make all tabs pinned. pin-feature is useless now... maybe we create an unpin feature in the future
		// Find first unpinned tab
		const index = sessionTabs.value.findIndex((tab) => !tab.pinned);
		// If no unpinned tab found, add to the end
		if (index === -1) {
			sessionTabs.value.push(tab);
			return;
		} else {
			// Otherwise, remove the unpinned tab and insert the new tab
			sessionTabs.value.splice(index, 1, tab);
		}
	};

	/**
	 * @deprecated
	 */
	const pinTab = (index: number) => {
		sessionTabs.value[index].pinned = true;
	};

	/**
	 * @param navigate - Whether to automatically navigate to the previous tab after closing the current tab or to '/' if there are no tabs left
	 */
	const removeTab = (index: number, navigate: boolean = true) => {
		// Navigate to the previous tab
		if (navigate) {
			const previousPath = previousUrl.value;
			if (previousPath && sessionTabs.value.some((tab) => tab.fullPath === previousPath)) {
				push(previousPath);
			} else if (previousPath && sessionTabs.value.length > 1) {
				// -1 because you still have your own tab open
				push(sessionTabs.value[sessionTabs.value.length - 2].fullPath);
			} else {
				push("/");
			}
		}
		sessionTabs.value.splice(index, 1);
	};

	/**
	 * @deprecated
	 */
	const pinCurrentTab = () => {
		const index = sessionTabs.value.findIndex((t) => t.fullPath.includes(currentRoute.value.fullPath));
		if (index !== -1) {
			pinTab(index);
		}
	};

	const setCurrentTabTitle = (...args: string[]) => {
		const tab = sessionTabs.value.find((t) => t.fullPath.includes(currentRoute.value.fullPath));
		if (tab) {
			tab.title = args.join(" ");
		}
	};

	/**
	 * @param navigate - Whether to automatically navigate to the previous tab after closing the current tab or to '/' if there are no tabs left
	 */
	const closeCurrentTab = async (navigate: boolean = true) => {
		const index = sessionTabs.value.findIndex((t) => t.fullPath.includes(currentRoute.value.fullPath));
		if (index !== -1) {
			await removeTab(index, navigate);
		}
	};

	return {
		sessionTabs,
		setCurrentTabTitle,
		pinTab,
		pinCurrentTab,
		removeTab,
		addTab,
		closeCurrentTab,
	};
};
